import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useKpiSummary, useEvaluationTrend } from '../hooks/useStylistPerformance.js';
import { HistoryTab } from '../components/stylist-performance/HistoryTab.js';
import { TOOLTIP_CONTENT_STYLE, AXIS_TICK_STYLE, GRID_PROPS } from '../lib/chart-theme.js';

/** @graph-connects none */
function formatMonth(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - offset);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** @graph-connects none */
const MONTH_OPTIONS = Array.from({ length: 6 }, (_, i) => formatMonth(i));

/** @graph-connects none */
const PERIOD_OPTIONS = [
  { label: '3M', value: 3 },
  { label: '6M', value: 6 },
  { label: '12M', value: 12 },
] as const;

/** @graph-connects none */
interface KpiCardProps {
  label: string;
  value: number | null;
  unit: string;
  prevValue: number | null;
  higherIsBetter?: boolean;
  format?: (v: number) => string;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business KPIサマリカード。値と前月比差分を表示する。差分の色分け（改善=緑、悪化=赤）。
 * @graph-connects none
 */
function KpiCard({ label, value, unit, prevValue, higherIsBetter = true, format }: KpiCardProps) {
  const { t } = useTranslation();
  const delta = value !== null && prevValue !== null ? value - prevValue : null;
  const isPositive = delta !== null && (higherIsBetter ? delta >= 0 : delta <= 0);
  const formatted = value !== null ? (format ? format(value) : String(value)) : '—';
  const deltaText =
    delta !== null
      ? t('stylistPerformance.vsPrevMonth', {
          delta: `${delta >= 0 ? '+' : ''}${unit === '%' ? `${delta.toFixed(1)}pt` : `${delta.toFixed(1)}${unit}`}`,
        })
      : '';

  return (
    <Card className="p-4">
      <p className="mb-1 text-[11px] font-semibold tracking-wider text-[var(--color-text-muted)] uppercase">
        {label}
      </p>
      <p className="text-2xl font-bold tabular-nums leading-tight text-[var(--color-text-primary)]">
        {formatted}
        {value !== null && (
          <span className="ml-0.5 text-base font-medium text-[var(--color-text-secondary)]">
            {unit}
          </span>
        )}
      </p>
      {deltaText && (
        <p
          className={`mt-1 text-xs font-semibold tabular-nums ${isPositive ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}
        >
          {deltaText}
        </p>
      )}
    </Card>
  );
}

/** @graph-connects none */
function formatPercent(v: string | number): string {
  return `${Number(v).toFixed(1)}%`;
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリストパフォーマンス分析ダッシュボード。KPIサマリ・評価推移チャート（ダッシュボードタブ）とレンタル履歴一覧（振り返りタブ）を表示する。
 * @graph-connects useKpiSummary [calls] KPIサマリ取得
 * @graph-connects useEvaluationTrend [calls] 評価推移取得
 * @graph-connects HistoryTab [calls] 振り返りタブ
 */
export function StylistPerformancePage() {
  const { t } = useTranslation();
  /** @graph-connects none */
  const [month, setMonth] = useState(MONTH_OPTIONS[0] ?? '');
  /** @graph-connects none */
  const [stylistId, setStylistId] = useState('');
  /** @graph-connects none */
  const [trendMonths, setTrendMonths] = useState(6);
  /** @graph-connects none */
  const [activeTab, setActiveTab] = useState<'dashboard' | 'history'>('dashboard');

  const stylistIdParam = stylistId.trim() || undefined;

  const { data: kpiData, isLoading: kpiLoading } = useKpiSummary(month, stylistIdParam);
  const { data: trendData, isLoading: trendLoading } = useEvaluationTrend(
    trendMonths,
    stylistIdParam,
  );

  const chartData = useMemo(() => {
    if (!trendData) return [];
    const overallMap = new Map(trendData.overall.map((r) => [r.month, r.goodRate]));
    return trendData.individual.map((r) => ({
      month: r.month.slice(2),
      goodRate: r.goodRate,
      overallRate: overallMap.get(r.month) ?? null,
      goodCount: r.goodCount,
      badCount: r.badCount,
    }));
  }, [trendData]);

  /** @graph-connects none */
  const tabCls = (key: string) =>
    `px-5 py-2.5 text-[13px] font-semibold cursor-pointer border-b-2 transition-colors ${
      activeTab === key
        ? 'text-[var(--color-accent-primary)] border-[var(--color-accent-primary)]'
        : 'text-[var(--color-text-muted)] border-transparent hover:text-[var(--color-text-secondary)]'
    }`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('stylistPerformance.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t('stylistPerformance.description')}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label
            htmlFor="month-select"
            className="text-xs font-semibold text-[var(--color-text-secondary)]"
          >
            {t('stylistPerformance.month')}
          </label>
          <select
            id="month-select"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
            }}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-raised)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)]"
          >
            {MONTH_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="stylist-filter"
            className="text-xs font-semibold text-[var(--color-text-secondary)]"
          >
            {t('stylistPerformance.stylist')}
          </label>
          <input
            id="stylist-filter"
            type="text"
            value={stylistId}
            onChange={(e) => {
              setStylistId(e.target.value);
            }}
            placeholder={t('stylistPerformance.stylistFilterPlaceholder')}
            className="w-48 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-raised)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-primary)]"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-[var(--color-border)]">
        <button
          type="button"
          className={tabCls('dashboard')}
          onClick={() => {
            setActiveTab('dashboard');
          }}
        >
          {t('stylistPerformance.dashboardTab')}
        </button>
        <button
          type="button"
          className={tabCls('history')}
          onClick={() => {
            setActiveTab('history');
          }}
        >
          {t('stylistPerformance.historyTab')}
        </button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          {/* KPI Cards */}
          {kpiLoading ? (
            <div className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</div>
          ) : kpiData ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <KpiCard
                label={t('stylistPerformance.stylingCount')}
                value={kpiData.current.stylingCount}
                unit={t('stylistPerformance.countUnit')}
                prevValue={kpiData.prevMonth.stylingCount}
                format={(v) => String(Math.round(v))}
              />
              <KpiCard
                label={t('stylistPerformance.goodRate')}
                value={kpiData.current.goodRate}
                unit="%"
                prevValue={kpiData.prevMonth.goodRate}
                format={(v) => v.toFixed(1)}
              />
              <KpiCard
                label={t('stylistPerformance.fitRate')}
                value={kpiData.current.fitRate}
                unit="%"
                prevValue={kpiData.prevMonth.fitRate}
                format={(v) => v.toFixed(1)}
              />
              <KpiCard
                label={t('stylistPerformance.purchaseRate')}
                value={kpiData.current.purchaseRate}
                unit="%"
                prevValue={kpiData.prevMonth.purchaseRate}
                format={(v) => v.toFixed(1)}
              />
              <KpiCard
                label={t('stylistPerformance.avgSelectionTime')}
                value={kpiData.current.avgProcessingTimeMin}
                unit={t('stylistPerformance.minUnit')}
                prevValue={kpiData.prevMonth.avgProcessingTimeMin}
                higherIsBetter={false}
                format={(v) => v.toFixed(1)}
              />
            </div>
          ) : (
            <div className="text-sm text-[var(--color-text-muted)]">{t('common.noData')}</div>
          )}

          {/* Evaluation Trend Chart */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('stylistPerformance.evaluationTrend')}
              </h3>
              <div className="flex gap-1">
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setTrendMonths(opt.value);
                    }}
                    className={`rounded px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                      trendMonths === opt.value
                        ? 'bg-[var(--color-accent-primary)] text-white'
                        : 'border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {trendLoading ? (
              <div className="flex h-64 items-center justify-center text-sm text-[var(--color-text-muted)]">
                {t('app.loading')}
              </div>
            ) : chartData.length > 0 ? (
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 16, right: 16, bottom: 4, left: 8 }}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="month" tick={AXIS_TICK_STYLE} tickLine={false} />
                    <YAxis
                      domain={[0, 100]}
                      tick={AXIS_TICK_STYLE}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatPercent}
                    />
                    <Tooltip
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      formatter={(value, name) => [
                        formatPercent(value as string | number),
                        name === 'goodRate'
                          ? t('stylistPerformance.goodRateIndividual')
                          : t('stylistPerformance.goodRateOverall'),
                      ]}
                      labelFormatter={(label) => `20${String(label)}`}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11 }}
                      formatter={(value: string) =>
                        value === 'goodRate'
                          ? t('stylistPerformance.goodRateIndividual')
                          : t('stylistPerformance.goodRateOverall')
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="goodRate"
                      name="goodRate"
                      stroke="var(--color-success)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      label={{
                        position: 'top' as const,
                        fontSize: 10,
                        fill: 'var(--color-success)',
                        formatter: (v: unknown) => `${String(Number(v).toFixed(1))}%`,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="overallRate"
                      name="overallRate"
                      stroke="var(--color-success)"
                      strokeWidth={1.5}
                      strokeDasharray="5 4"
                      dot={false}
                      strokeOpacity={0.4}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center text-sm text-[var(--color-text-muted)]">
                {t('stylistPerformance.noRatingData')}
              </div>
            )}
          </Card>
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && <HistoryTab stylistId={stylistIdParam} />}
    </div>
  );
}
